import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { filterByWildCard } from '@app/shared/utility';
import { Subject } from 'rxjs';
import { MunicipalityKeys, MunicipalityListRow } from '../models/municipality-list-row';
import { takeUntil } from 'rxjs/operators';
import { MunicipalityStoreService } from '@app/shared/services/store/municipality-store.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-municipality-list-table',
  templateUrl: './municipality-list-table.component.html',
  styleUrls: ['./municipality-list-table.component.scss'],
})
export class MunicipalityListTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild(MatTable, ) table: MatTable<MunicipalityListRow> | undefined;
  @ViewChild(MatPaginator, )
  matPaginator: MatPaginator | null = null;
  @ViewChild(MatSort, ) matSort: MatSort | null = null;
  @Input() searchValue?: string;
  @Input() newData: any;

  selection = new SelectionModel<string>(true, []);
  dataSource!: MatTableDataSource<any[]>;

  count = 1;
  pageSize = 5;
  pageIndex = 0;
  pageSizeOptions: number[] = [5, 10, 20];
  isLoading = true;
  data?: MunicipalityKeys;

  columnsToDisplay: string[] = ['checkbox', 'status', 'municipalityNumber', 'municipalityName', 'zipCode', 'creationDate', 'modifyDate'];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private municipalityStoreService: MunicipalityStoreService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.municipalityStoreService.initializeMunicipalities();
    this.municipalityStoreService.allMunicipalities$.pipe(takeUntil(this.destroy$)).subscribe((municipalities) => {
      this.loadDataSource(municipalities);
      this.isLoading = false;
    });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) {
      this.dataSource.paginator = this.matPaginator;
      this.dataSource.sort = this.matSort;
    }
  }

  /**
   *
   * @param changes detects changes to our Input property
   */
  ngOnChanges(changes: SimpleChanges): void {
    const data: SimpleChange = changes['newData'];

    if (data) {
      this.data = data.currentValue;
      this.reloadTable();
    }

    if (changes.searchValue.currentValue) {
      this.dataSource.filter = changes.searchValue.currentValue;
    }

    if (changes.searchValue.currentValue === '') {
      this.dataSource.filter = '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   *
   * @param municipality data searchable values
   * @param query string to filter
   * @returns query string match
   */
  private filterByWildCard(municipality: any, query: string): boolean {
    return filterByWildCard(query, municipality.municipalityKey, municipality.name);
  }

  /**
   *
   * @param municipalities to load into MatTableDataSource
   */
  private loadDataSource(municipalities: any[]): void {
    this.count = municipalities.length;
    this.dataSource = new MatTableDataSource(municipalities);

    this.dataSource.filterPredicate = (data: any, filter: string) => this.filterByWildCard(data, filter);

    this.dataSource.sortingDataAccessor = (data: any, sortHeaderId: string) => {
      if (sortHeaderId === 'municipalityName') {
        data.name = `${data.name}`.toLowerCase();
        return data.name;
      }
      if (sortHeaderId === 'creationDate') {
        data.creationDate = `${data.creationDate}`;
        return data.creationDate;
      }
    };

    this.dataSource.paginator = this.matPaginator;
    this.dataSource.sort = this.matSort;
    this.isLoading = false;
  }

  /**
   * reloads dataSource if a municipality is added
   */
  private reloadTable(): void {
    if (this.data && this.data.added.length > 0) {
      this.isLoading = true;
      this.municipalityStoreService.initializeMunicipalities();
      this.municipalityStoreService.allMunicipalities$.pipe(takeUntil(this.destroy$)).subscribe((municipalities) => {
        this.matSort?.sort({
          id: '',
          start: 'desc',
          disableClear: false,
        } as MatSortable);
        this.matSort?.sort({
          id: 'creationDate',
          start: 'desc',
          disableClear: false,
        } as MatSortable);

        this.loadDataSource(municipalities);
      });
    }
  }

  /**
   *
   * @param key municipalityKey to iterate from
   * @returns true or false for icon display
   */
  showRecentDataIcon(key: string): boolean {
    if (this.data !== undefined) {
      return this.data.added.some((el) => el.municipalityKey === key);
    }

    return false;
  }
}
